/**
 * Magic-byte sniffing of the two derivative formats the API serves (ADR-0009 #3: PNG for logos,
 * WebP or PNG for billboards). The renderer checks the bytes it received match the declared
 * content type and reads the declared pixel size from the header BEFORE decoding, so a wrong or
 * oversized file is refused without handing it to the image decoder (defence in depth: the server
 * already re-encoded and bounded it).
 */
export type SniffedType = "image/png" | "image/webp";

export interface SniffResult {
  type: SniffedType;
  width: number;
  height: number;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function ascii(b: Uint8Array, at: number, text: string): boolean {
  if (b.length < at + text.length) return false;
  for (let i = 0; i < text.length; i++) if (b[at + i] !== text.charCodeAt(i)) return false;
  return true;
}

function u16le(b: Uint8Array, at: number): number {
  return b[at]! | (b[at + 1]! << 8);
}

function u24le(b: Uint8Array, at: number): number {
  return b[at]! | (b[at + 1]! << 8) | (b[at + 2]! << 16);
}

function u32be(b: Uint8Array, at: number): number {
  return ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
}

function sniffPng(b: Uint8Array): SniffResult | null {
  if (b.length < 24) return null;
  for (let i = 0; i < PNG_SIGNATURE.length; i++) if (b[i] !== PNG_SIGNATURE[i]) return null;
  // The first chunk must be IHDR (length 13): width and height are big-endian u32.
  if (u32be(b, 8) !== 13 || !ascii(b, 12, "IHDR")) return null;
  const width = u32be(b, 16);
  const height = u32be(b, 20);
  return width > 0 && height > 0 ? { type: "image/png", width, height } : null;
}

function sniffWebp(b: Uint8Array): SniffResult | null {
  if (b.length < 30 || !ascii(b, 0, "RIFF") || !ascii(b, 8, "WEBP")) return null;
  let width = 0;
  let height = 0;
  if (ascii(b, 12, "VP8X")) {
    // Extended: 24-bit canvas width-1 / height-1.
    width = u24le(b, 24) + 1;
    height = u24le(b, 27) + 1;
  } else if (ascii(b, 12, "VP8L")) {
    // Lossless: signature 0x2f, then 14 bits width-1 and 14 bits height-1.
    if (b[20] !== 0x2f) return null;
    const bits = (b[21]! | (b[22]! << 8) | (b[23]! << 16) | (b[24]! << 24)) >>> 0;
    width = (bits & 0x3fff) + 1;
    height = ((bits >>> 14) & 0x3fff) + 1;
  } else if (ascii(b, 12, "VP8 ")) {
    // Lossy: key frame start code 9d 01 2a, then 14-bit width / height (+ 2 scale bits).
    if (b[23] !== 0x9d || b[24] !== 0x01 || b[25] !== 0x2a) return null;
    width = u16le(b, 26) & 0x3fff;
    height = u16le(b, 28) & 0x3fff;
  } else {
    return null;
  }
  return width > 0 && height > 0 ? { type: "image/webp", width, height } : null;
}

/** The real format and declared size of a PNG or WebP file, or null for anything else. */
export function sniffImage(bytes: Uint8Array): SniffResult | null {
  return sniffPng(bytes) ?? sniffWebp(bytes);
}
